import React from 'react';
import { WIDGET_ICON_MAP } from '../../../widgetIconMap';
import LeyningDisplay from '../../../shared/LeyningDisplay';

const timeToMinutes = (s) => {
    if (!s || s === '--:--') return -1;
    const [h, m] = s.split(':').map(Number);
    return h * 60 + m;
};

const buildTimesByDay = (processedCustomTimes) => {
    const map = {};
    (processedCustomTimes || []).forEach(ct => {
        const idx = ct.dayIndex ?? 0;
        if (!map[idx]) map[idx] = [];
        map[idx].push(ct);
    });
    Object.values(map).forEach(g => g.sort((a, b) => (a._sortKey ?? timeToMinutes(a.time)) - (b._sortKey ?? timeToMinutes(b.time))));
    return map;
};

const StateScreen = ({ msg, isError }) => (
    <div style={{
        width: '100%', height: '100%',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        backgroundColor: '#0f1e30', direction: 'rtl',
    }}>
        <span style={{ fontSize: '1.125rem', color: isError ? '#f87171' : 'rgba(232,220,200,0.5)' }}>{msg}</span>
    </div>
);

const Elegant = ({
    moadData, processedCustomTimes, loading, error,
    title = 'מועד', showTitle = true, days = [],
    entryIcon = 'Sun', exitIcon = 'Star', dayLabels = [],
    drashotByDay = {},
    showLeyning, showHaftarah, leyningDisplayMode,
}) => {
    const EntryIcon = WIDGET_ICON_MAP[entryIcon] || WIDGET_ICON_MAP['Sun'];
    const ExitIcon  = WIDGET_ICON_MAP[exitIcon]  || WIDGET_ICON_MAP['Star'];

    if (loading) return <StateScreen msg="טוען זמני מועד..." />;
    if (error)   return <StateScreen msg={error} isError />;
    if (!moadData?.days?.length) return <StateScreen msg="אין נתוני מועד" />;

    const firstDay = moadData.days[0];
    const lastDay  = moadData.days[moadData.days.length - 1];
    const entryTime = firstDay?.entryTime || '--:--';
    const exitTime  = lastDay?.exitTime   || '--:--';
    const entryLabel = dayLabels[0]?.entry || "זמן א'";
    const exitLabel  = dayLabels[moadData.days.length - 1]?.exit || "זמן ב'";
    const EffectiveEntryIcon = WIDGET_ICON_MAP[dayLabels[0]?.entryIcon] || EntryIcon;
    const EffectiveExitIcon  = WIDGET_ICON_MAP[dayLabels[moadData.days.length - 1]?.exitIcon] || ExitIcon;
    const timesByDay = buildTimesByDay(processedCustomTimes);
    const hasCustomTimes = (processedCustomTimes || []).length > 0;
    const showEntry = dayLabels[0]?.entry !== null;
    const showExit  = dayLabels[moadData.days.length - 1]?.exit !== null;

    return (
        <div style={{
            width: '100%', height: '100%',
            display: 'flex', flexDirection: 'column',
            direction: 'rtl', boxSizing: 'border-box',
            background: 'linear-gradient(160deg, #162540 0%, #0c1a2e 100%)',
            overflow: 'hidden',
        }}>
            {/* Title */}
            {showTitle && (
                <div style={{
                    padding: '20px 28px 16px',
                    borderBottom: '1px solid rgba(212,168,75,0.15)',
                    flexShrink: 0,
                }}>
                    <span style={{
                        fontSize: '1.5rem', fontWeight: 700,
                        color: '#d4a84b', letterSpacing: '0.04em',
                    }}>
                        {title}
                    </span>
                </div>
            )}

            {/* Entry row */}
            {showEntry && (
                <div style={{
                    display: 'flex', alignItems: 'center',
                    padding: '20px 28px',
                    borderBottom: showExit ? '1px solid rgba(255,255,255,0.06)' : 'none',
                    gap: '20px', flexShrink: 0,
                }}>
                    <div style={{
                        width: 44, height: 44, borderRadius: '50%',
                        backgroundColor: 'rgba(212,168,75,0.15)',
                        border: '1px solid rgba(212,168,75,0.35)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                    }}>
                        <EffectiveEntryIcon style={{ width: 20, height: 20, color: '#d4a84b' }} />
                    </div>
                    <span style={{ fontSize: '1.1rem', fontWeight: 500, color: 'rgba(232,220,200,0.65)', flex: 1 }}>
                        {entryLabel}
                    </span>
                    <span style={{
                        fontSize: '2.75rem', fontWeight: 700,
                        color: '#e8dcc8', letterSpacing: '0.02em',
                        fontVariantNumeric: 'tabular-nums', direction: 'ltr',
                        lineHeight: 1,
                    }}>
                        {entryTime}
                    </span>
                </div>
            )}

            {/* Divider with dot */}
            {showEntry && showExit && (
                <div style={{
                    display: 'flex', alignItems: 'center',
                    padding: '0 28px', flexShrink: 0,
                }}>
                    <div style={{ flex: 1, height: 1, backgroundColor: 'rgba(212,168,75,0.12)' }} />
                    <div style={{
                        width: 6, height: 6, borderRadius: '50%',
                        backgroundColor: 'rgba(212,168,75,0.4)',
                        margin: '0 12px',
                    }} />
                    <div style={{ flex: 1, height: 1, backgroundColor: 'rgba(212,168,75,0.12)' }} />
                </div>
            )}

            {/* Exit row */}
            {showExit && (
                <div style={{
                    display: 'flex', alignItems: 'center',
                    padding: '20px 28px',
                    borderBottom: hasCustomTimes ? '1px solid rgba(255,255,255,0.06)' : 'none',
                    gap: '20px', flexShrink: 0,
                }}>
                    <div style={{
                        width: 44, height: 44, borderRadius: '50%',
                        backgroundColor: 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(255,255,255,0.12)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                    }}>
                        <EffectiveExitIcon style={{ width: 20, height: 20, color: 'rgba(232,220,200,0.6)' }} />
                    </div>
                    <span style={{ fontSize: '1.1rem', fontWeight: 500, color: 'rgba(232,220,200,0.65)', flex: 1 }}>
                        {exitLabel}
                    </span>
                    <span style={{
                        fontSize: '2.75rem', fontWeight: 700,
                        color: '#e8dcc8', letterSpacing: '0.02em',
                        fontVariantNumeric: 'tabular-nums', direction: 'ltr',
                        lineHeight: 1,
                    }}>
                        {exitTime}
                    </span>
                </div>
            )}

            {/* דרשות */}
            {moadData.days.some((_, i) => drashotByDay[i]) && (
                <div style={{
                    padding: '12px 28px',
                    borderBottom: hasCustomTimes ? '1px solid rgba(255,255,255,0.06)' : 'none',
                    flexShrink: 0,
                }}>
                    {moadData.days.map((day, dayIdx) => {
                        const drasha = drashotByDay[dayIdx];
                        if (!drasha) return null;
                        const dayColor = days[dayIdx]?.color || '#d4a84b';
                        return (
                            <div key={`drasha-${dayIdx}`} style={{
                                padding: '10px 16px',
                                borderRadius: 8,
                                backgroundColor: 'rgba(212,168,75,0.06)',
                                border: '1px solid rgba(212,168,75,0.18)',
                                marginBottom: 10,
                                textAlign: 'center',
                            }}>
                                {moadData.days.length > 1 && (
                                    <div style={{
                                        fontSize: '0.7rem', fontWeight: 700,
                                        color: dayColor, letterSpacing: '0.08em',
                                        marginBottom: 4, opacity: 0.85,
                                    }}>
                                        {day.label}
                                    </div>
                                )}
                                <div style={{
                                    fontSize: '1rem', fontWeight: 500,
                                    color: '#e8dcc8', fontStyle: 'italic',
                                    whiteSpace: 'pre-wrap', lineHeight: 1.5,
                                }}>
                                    {drasha}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Leyning */}
            {(showLeyning || showHaftarah) && moadData.days.some(d => d.leyning) && (
                <div style={{
                    padding: '12px 28px',
                    borderBottom: hasCustomTimes ? '1px solid rgba(255,255,255,0.06)' : 'none',
                    flexShrink: 0,
                }}>
                    {moadData.days.map((day, dayIdx) => {
                        if (!day.leyning) return null;
                        const dayColor = days[dayIdx]?.color || '#d4a84b';
                        return (
                            <div key={`leyning-${dayIdx}`} style={{ marginBottom: 10 }}>
                                {moadData.days.length > 1 && (
                                    <div style={{
                                        fontSize: '0.7rem', fontWeight: 700,
                                        color: dayColor, letterSpacing: '0.08em',
                                        marginBottom: 4, opacity: 0.85,
                                    }}>
                                        {day.label}
                                    </div>
                                )}
                                <LeyningDisplay
                                    leyning={day.leyning}
                                    mode={leyningDisplayMode}
                                    showHaftarah={showHaftarah}
                                    showMaftir={leyningDisplayMode === 'full'}
                                    compact={true}
                                    style={{ padding: '8px 0' }}
                                />
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Custom times */}
            {hasCustomTimes && (
                <div style={{
                    flex: 1, overflowY: 'auto',
                    padding: '16px 28px',
                }}>
                    {moadData.days.map((day, dayIdx) => {
                        const dayTimes = timesByDay[dayIdx];
                        if (!dayTimes?.length) return null;
                        const dayColor = days[dayIdx]?.color || '#d4a84b';
                        return (
                            <div key={dayIdx} style={{ marginBottom: 16 }}>
                                <div style={{
                                    fontSize: '0.7rem', fontWeight: 700,
                                    color: dayColor, letterSpacing: '0.1em',
                                    textTransform: 'uppercase', marginBottom: 8,
                                    opacity: 0.85,
                                }}>
                                    {day.label}
                                </div>
                                {dayTimes.map((ct, idx) => (
                                    <div key={idx} style={{
                                        display: 'flex', alignItems: 'center',
                                        justifyContent: 'space-between',
                                        padding: '7px 0',
                                        borderBottom: idx < dayTimes.length - 1
                                            ? '1px solid rgba(255,255,255,0.05)'
                                            : 'none',
                                    }}>
                                        <span style={{ fontSize: '1rem', color: 'rgba(232,220,200,0.6)', fontWeight: 400 }}>
                                            {ct.label}
                                        </span>
                                        <span style={{
                                            fontSize: '1.25rem', fontWeight: 600,
                                            color: '#e8dcc8',
                                            fontVariantNumeric: 'tabular-nums', direction: 'ltr',
                                        }}>
                                            {ct.time}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default Elegant;
